import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { DrawCreditComponent } from '../alunos/matricula-aluno/aluno-financeiro-contrato/matricula-caracteristicas-plano/cartao-credito/draw-credit/draw-credit.component';
import { API } from 'src/app/services/api';
import { environment } from 'src/environments/environment';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import * as moment from 'moment';

export const MY_FORMATS_REGUA = {
  parse: { dateInput: 'MM/YYYY' },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-regua-pagamento-publico',
  templateUrl: './regua-pagamento-publico.component.html',
  styleUrls: ['./regua-pagamento-publico.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS_REGUA },
  ]
})
export class ReguaPagamentoPublicoComponent implements OnInit, OnDestroy {
  @ViewChild('drawCreditCard') drawCreditCard: DrawCreditComponent;

  private destroy$ = new Subject<void>();
  loading: boolean = true;
  erro: boolean = false;
  invalido: boolean = false;
  pago: boolean = false;

  nome: string = '';
  valor: number = 0;
  dataVencimento: string = '';
  unidade: string = '';
  logoUrl: string = '';
  linhaDigitavel: string = '';
  pixCodigo: string = '';
  pixBase64: string = '';

  // Toggle de metodo de pagamento
  metodoSelecionado: string = 'boleto'; // 'boleto' | 'pix' | 'cartao'

  // Copiar estados
  codigoCopiado: boolean = false;
  pixCopiado: boolean = false;
  linkCopiado: boolean = false;

  // Cartao
  cartaoForm: FormGroup;
  cardData: any = {};
  processandoCartao: boolean = false;
  erroCartao: string = '';
  pagamentoCartaoSucesso: boolean = false;
  transacaoId: string = '';
  dataHoraPagamento: Date = null;
  maxParcelas: number = 1;
  opcoesParcelamento: number[] = [1];

  minDateValidade = moment().startOf('month');

  private linkToken: string = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private fb: FormBuilder
  ) {
    this.cartaoForm = this.fb.group({
      numero:   [null, [Validators.required, Validators.pattern(/^\d{16}$/)]],
      nome:     [null, [Validators.required, Validators.maxLength(50)]],
      validade: [null, [Validators.required]],
      codigo:   [null, [Validators.required, Validators.pattern(/^\d{3,4}$/)]],
      parcelas: [1, Validators.required],
      informarCpfTitular: [false],
      cpfTitular: [null]
    });
  }

  ngOnInit(): void {
    this.cartaoForm.valueChanges.pipe(
      distinctUntilChanged((a, b) =>
        a.numero === b.numero &&
        a.nome === b.nome &&
        a.validade === b.validade &&
        a.codigo === b.codigo
      ),
      takeUntil(this.destroy$)
    ).subscribe(val => {
      this.cardData = {
        numero: (val.numero || '').replace(/\s/g, ''),
        nome: val.nome,
        validade: val.validade ? moment(val.validade).format('MM/YYYY') : null,
        codigo: val.codigo
      };
    });

    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.invalido = true;
      this.loading = false;
      return;
    }
    this.linkToken = token;
    this.carregarPagamento(token);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarPagamento(token: string): void {
    const url = `${environment.baseUrl}${API.reguaPagamento.buscar}/${token}`;
    this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        if (!response) {
          this.invalido = true;
        } else {
          this.nome = response.nome || 'Aluno';
          this.valor = response.valor || 0;
          this.dataVencimento = response.dataVencimento || '';
          this.unidade = response.unidade || '';
          this.logoUrl = response.logoUrl || '';
          this.linhaDigitavel = response.linhaDigitavel || '';
          this.pixCodigo = response.pixCodigo || '';
          this.pixBase64 = response.pixBase64 || '';
          this.pago = response.pago || false;
          this.maxParcelas = response.maxParcelas || 1;
          this.opcoesParcelamento = Array.from({ length: this.maxParcelas }, (_, i) => i + 1);
          this.cartaoForm.patchValue({ parcelas: 1 });
          // Selecionar metodo padrão: pix se disponivel, senão boleto
          this.metodoSelecionado = this.pixCodigo ? 'pix' : 'boleto';
        }
        this.loading = false;
      },
      (error) => {
        if (error.status === 404) {
          this.invalido = true;
        } else {
          this.erro = true;
        }
        this.loading = false;
      }
    );
  }

  selecionarMetodo(metodo: string): void {
    this.metodoSelecionado = metodo;
    this.erroCartao = '';
  }

  copiarCodigo(): void {
    if (navigator.clipboard && this.linhaDigitavel) {
      navigator.clipboard.writeText(this.linhaDigitavel).then(() => {
        this.codigoCopiado = true;
        setTimeout(() => this.codigoCopiado = false, 3000);
      });
    }
  }

  copiarPix(): void {
    if (navigator.clipboard && this.pixCodigo) {
      navigator.clipboard.writeText(this.pixCodigo).then(() => {
        this.pixCopiado = true;
        setTimeout(() => this.pixCopiado = false, 3000);
      });
    }
  }

  copiarLink(): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.linkCopiado = true;
        setTimeout(() => this.linkCopiado = false, 3000);
      });
    }
  }

  closeDatePicker(dateString: string, dp: any): void {
    const control = this.cartaoForm.get('validade');
    const date = moment(dateString);
    date.endOf('month');
    control.setValue(date);
    dp.close();
  }

  pagarComCartao(): void {
    if (this.cartaoForm.invalid || this.processandoCartao) return;

    this.processandoCartao = true;
    this.erroCartao = '';

    const form = this.cartaoForm.value;
    const validade = moment(form.validade);

    const payload = {
      numeroCartao: (form.numero || '').replace(/\s/g, ''),
      nomeTitular: form.nome,
      mesValidade: validade.month().toString(), // 0-indexed (backend faz +1)
      anoValidade: validade.year().toString(),
      codigoSeguranca: form.codigo,
      quantidadeParcelas: form.parcelas,
      cpfTitularCartao: form.informarCpfTitular && form.cpfTitular
        ? form.cpfTitular.replace(/\D/g, '') : null
    };

    const url = `${environment.baseUrl}${API.reguaPagamento.pagarCartao}/${this.linkToken}/PagarCartao`;
    this.http.post<any>(url, payload).pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        this.processandoCartao = false;
        if (response && response.retornoCartao === 1) {
          this.pagamentoCartaoSucesso = true;
          this.pago = true;
          this.transacaoId = response.transacaoId || response.id || '';
          this.dataHoraPagamento = new Date();
        } else {
          this.erroCartao = this.mapearErroCartao(response?.retornoCartao);
        }
      },
      (error) => {
        this.processandoCartao = false;
        this.erroCartao = error?.error?.mensagem || error?.error?.Mensagem || 'Erro ao processar pagamento. Tente novamente.';
      }
    );
  }

  private mapearErroCartao(codigo: number): string {
    switch (codigo) {
      case 2:  return 'Transação não autorizada pelo banco emissor.';
      case 3:  return 'Cartão expirado. Verifique a data de validade.';
      case 4:  return 'Cartão bloqueado. Entre em contato com o banco.';
      case 5:  return 'Tempo esgotado. Tente novamente.';
      case 6:  return 'Cartão cancelado. Use outro cartão.';
      case 7:  return 'Verifique os dados do cartão e tente novamente.';
      case 8:  return 'Transação já confirmada anteriormente.';
      case 9:  return 'Limite de transações excedido.';
      case 10: return 'Validade do cartão expirada.';
      case 11: return 'Código de segurança inválido.';
      default: return 'Erro ao processar pagamento. Tente novamente.';
    }
  }
}
